import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';
import type {
  UserProfileUpsertRequest,
  UserProfileResponse,
  UserProfileActionResponse,
  ChartDataRequest,
  ChartDataResponse,
  UpdateMeasurementRequest,
  UserRelationRequest,
  InviteRequest,
  GetUserConnectionsRequest,
  GetUserConnectionsResponse,
  GetBlockedUsersResponse,
  BuddySearchRequest,
  BuddySearchResponse,
  UpdateProfilePrivacySettingsResponse,
  AccountSettingsResponseApiResponse,
  AccountSettingsUpdateRequest,
  LeaderBoardListRequest,
  LeaderBoardListResponse,
  AwardsResponse,
  ReportUserRequest,
  GetUserBuddyPreferencesResponse,
  BuddyMatchingRequest,
  BuddyMatchingResponse,
} from '../../types';
import type { AxiosResponse } from 'axios';

const userProfileApi = {
  getProfile: (): Promise<AxiosResponse<UserProfileResponse>> =>
    apiClient.get<UserProfileResponse>(API_ENDPOINTS.USER_PROFILE.GET_MY_PROFILE),

  updateProfilePrivacySettings: (payload: FormData): Promise<AxiosResponse<UpdateProfilePrivacySettingsResponse>> =>
    apiClient.post<UpdateProfilePrivacySettingsResponse>(
      API_ENDPOINTS.USER_PROFILE.UPSERT_PROFILE_PRIVACY_SETTINGS,
      payload,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ),

  upsertProfile: (payload: UserProfileUpsertRequest | FormData): Promise<AxiosResponse<UserProfileResponse>> => {
    const headers = payload instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined;
    return apiClient.post<UserProfileResponse>(
      API_ENDPOINTS.USER_PROFILE.UPSERT_PROFILE,
      payload,
      { headers },
    );
  },

  getUserInfo: (): Promise<AxiosResponse<UserProfileResponse>> =>
    apiClient.get<UserProfileResponse>(API_ENDPOINTS.USER_PROFILE.GET_OTHER_USER_PROFILE),

  getOtherUserProfile: (profileUserGuid: string): Promise<AxiosResponse<UserProfileResponse>> => {
    if (!profileUserGuid || !String(profileUserGuid).trim()) {
      return Promise.reject(new Error('Profile user GUID is required.'));
    }
    return apiClient.get<UserProfileResponse>(
      API_ENDPOINTS.USER_PROFILE.GET_OTHER_USER_PROFILE,
      { params: { profileUserGuid: String(profileUserGuid).trim() } },
    );
  },

  getChartData: (payload: ChartDataRequest): Promise<AxiosResponse<ChartDataResponse>> =>
    apiClient.post<ChartDataResponse>(
      API_ENDPOINTS.USER_PROFILE.GET_CHART_DATA,
      payload,
    ),

  updateMeasurement: (payload: UpdateMeasurementRequest): Promise<AxiosResponse<UserProfileResponse>> =>
    apiClient.post<UserProfileResponse>(
      API_ENDPOINTS.USER_PROFILE.UPDATE_USER_MEASUREMENT,
      payload,
    ),

  userRelation: (payload: UserRelationRequest): Promise<AxiosResponse<UserProfileActionResponse>> =>
    apiClient.post<UserProfileActionResponse>(
      API_ENDPOINTS.USER_PROFILE.USER_RELATION,
      payload,
    ),

  updateInvite: (payload: InviteRequest): Promise<AxiosResponse<UserProfileActionResponse>> =>
    apiClient.put<UserProfileActionResponse>(
      API_ENDPOINTS.USER_PROFILE.INVITE_STATUS,
      payload,
    ),

  /** `signal` lets a caller drop a superseded list request (tab switch, new search). */
  getUserConnections: (
    payload: GetUserConnectionsRequest,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<GetUserConnectionsResponse>> =>
    apiClient.post<GetUserConnectionsResponse>(
      API_ENDPOINTS.USER_PROFILE.GET_USER_CONNECTIONS,
      payload,
      signal ? { signal } : undefined,
    ),

  getBlockedUsers: (): Promise<AxiosResponse<GetBlockedUsersResponse>> =>
    apiClient.get<GetBlockedUsersResponse>(API_ENDPOINTS.USER_PROFILE.USER_RELATION, {
      params: { relation: 'block' },
    }),

  buddySearch: (payload: BuddySearchRequest): Promise<AxiosResponse<BuddySearchResponse>> =>
    apiClient.post<BuddySearchResponse>(API_ENDPOINTS.USER_PROFILE.BUDDY_SEARCH, payload),

  getAccountSettings: (): Promise<AxiosResponse<AccountSettingsResponseApiResponse>> =>
    apiClient.get<AccountSettingsResponseApiResponse>(API_ENDPOINTS.USER_PROFILE.GET_ACCOUNT_SETTINGS),

  updateAccountSettings: (payload: AccountSettingsUpdateRequest): Promise<AxiosResponse<AccountSettingsResponseApiResponse>> =>
    apiClient.post<AccountSettingsResponseApiResponse>(API_ENDPOINTS.USER_PROFILE.UPDATE_ACCOUNT_SETTINGS, payload),

  getLeaderBoardList: (payload: LeaderBoardListRequest): Promise<AxiosResponse<LeaderBoardListResponse>> =>
    apiClient.post<LeaderBoardListResponse>(API_ENDPOINTS.USER_PROFILE.LEADERBOARD_LIST, payload),

  getAwards: (): Promise<AxiosResponse<AwardsResponse>> =>
    apiClient.get<AwardsResponse>(API_ENDPOINTS.USER_PROFILE.AWARDS),

  reportUser: (payload: ReportUserRequest): Promise<AxiosResponse<UserProfileActionResponse>> =>
    apiClient.post<UserProfileActionResponse>(API_ENDPOINTS.USER_PROFILE.REPORT_USER, payload),

  getUserBuddyPreferences: (): Promise<AxiosResponse<GetUserBuddyPreferencesResponse>> =>
    apiClient.get<GetUserBuddyPreferencesResponse>(API_ENDPOINTS.USER_PROFILE.GET_USER_BUDDY_PREFERENCES),

  buddyMatching: (payload: BuddyMatchingRequest): Promise<AxiosResponse<BuddyMatchingResponse>> =>
    apiClient.post<BuddyMatchingResponse>(
      API_ENDPOINTS.USER_PROFILE.BUDDY_MATCHING,
      payload,
    ),
};

export default userProfileApi;
